import { yupResolver } from "@hookform/resolvers/yup";
import base64 from "base-64";
import queryString from "query-string";
import { useCallback, useEffect, useRef, useState } from "react";
import TagManager from "react-gtm-module";
import { Helmet } from "react-helmet";
import { Controller, useForm } from "react-hook-form";
import { useHistory, useLocation } from "react-router-dom";
import styled from "styled-components";

import { Icon, icons } from "../../../components/Icon";
import { Turnstile } from "../../../components/Turnstile/Turnstile";
import { useSignUpLoading } from "../../../customHooks/useSignUpLoading";
import { createNotification } from "../../../modules/notificationManager";
import { pushAdTrackingEvent } from "../../../utils/adTracking";
import api from "../../../utils/api";
import { getErrorContext } from "../../../utils/authErrorHandler";
import { getFullName } from "../../../utils/helper";
import {
  getAuthRedirectPath,
  storeSocialAuthTokens,
} from "../../../utils/socialAuthHelpers";
import AuthTransition from "../shared/AuthTransition";
import GoogleAuthButton from "../shared/GoogleOAuth/GoogleAuthButton";
import { Logo } from "../shared/Logo";
import PasswordStrengthIndicator from "../shared/PasswordStrengthIndicator";
import {
  AuthCard,
  AuthContainer,
  AuthNavLink,
  AuthSection,
  AuthText,
  CloudflareBranding,
  CloudflareLogo,
  ErrorMessage,
  FooterSection,
  FormSection,
  HeaderSection,
  InputContainer,
  InputGroup,
  OrDivider,
  PasswordInputContainer,
  PasswordToggle,
  PrimaryButton,
  PrivacyTermsLink,
  PrivacyTermsLinks,
  ServerErrorAlert,
  StyledInput,
  Subtitle,
  SuccessBrandingSection,
  SuccessIndicator,
  SuccessText,
  TermsLink,
  Title,
} from "../shared/authStyles";
import {
  EyeIcon,
  EyeSlashIcon,
  TickCircleIcon,
} from "../shared/icons/AuthIcons";
import { AnimatePresence, motion } from "../shared/motionUtils";
import { signUpSchema } from "../shared/validationSchemas";

const SignUp = ({
  signUpRequest,
  resetSignUp,
  signup,
  resetSignUpError,
  socialAuth,
  googleAuthReset,
}) => {
  const history = useHistory();
  const location = useLocation();
  const [showPassword, setShowPassword] = useState(false);
  const [serverError, setServerError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [isLoaderOpen, setIsLoaderOpen] = useState(false);
  const [timezone, setTimezone] = useState("");
  const [invitedEmail, setInvitedEmail] = useState("");
  const [emailGiven, setEmailGiven] = useState(false);
  const [invitedKey, setInvitedKey] = useState("");
  const [invitedRequireSignUp, setInvitedRequireSignUp] = useState(false);
  const [fromJoinLink, setFromJoinLink] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [attributionData, setAttributionData] = useState({});
  const [query, setQuery] = useState(null);
  const passwordFieldRef = useRef(null);
  const fullNameRef = useRef(null);

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting, isSubmitted },
    watch,
    setValue,
    clearErrors,
  } = useForm({
    resolver: yupResolver(signUpSchema),
    mode: "onSubmit",
    defaultValues: {
      fullName: "",
      email: "",
      password: "",
    },
  });

  // Use custom hook for loading state management (after isSubmitting is defined)
  const { isLoading, setLoading } = useSignUpLoading(signup, isSubmitting);

  const passwordValue = watch("password");

  const tagManagerArgs = {
    gtmId: process.env.REACT_APP_GOOGLE_TAG_MANAGER_ID,
  };

  useEffect(() => {
    return () => {
      resetSignUpError();
    };
  }, []);

  // Check if user is already logged in
  useEffect(() => {
    let token = localStorage.getItem("access_token");
    if (token != null) {
      setIsLoaderOpen(true);
      api({
        url: `onboarding/check/`,
        method: "GET",
      })
        .then(response => {
          if (response.status === 200) {
            history.push("/user/dashboard");
          } else if (response.status === 401) {
            localStorage.clear();
            history.push("/auth/login");
          }
          setIsLoaderOpen(false);
        })
        .catch(err => {
          history.push("/user/dashboard");
          setIsLoaderOpen(false);
        });
    } else {
      if (location && location.search && location.search.length > 0) {
        const queryParsed = queryString.parse(location.search);
        setQuery(queryParsed);
        if (queryParsed.key) {
          try {
            let key = queryParsed.key;
            let payload = JSON.parse(base64.decode(key.split(".")[1]));
            if (payload && payload.signup) {
              history.push(`/accept-invitation?key=${key}`);
            } else {
              setInvitedRequireSignUp(true);
              setInvitedEmail(payload.email);
              setValue("email", payload.email);
              setInvitedKey(key);

              if (payload.invitation_type === "organization-url") {
                setFromJoinLink(true);
              }
            }
          } catch (error) {
            createNotification(
              "error",
              "Malformed url. You will not be able to join your desired organization.",
              5000,
            );
          }
        } else if (queryParsed.email) {
          setValue("email", queryParsed.email);
          setEmailGiven(true);
        }

        // Extract attribution data from query parameters
        const attributionParams = {};
        if (queryParsed.gclid) attributionParams.gclid = queryParsed.gclid;
        if (queryParsed.fbclid) attributionParams.fbclid = queryParsed.fbclid;
        if (queryParsed.utm_source)
          attributionParams.utm_source = queryParsed.utm_source;
        if (queryParsed.utm_campaign)
          attributionParams.utm_campaign = queryParsed.utm_campaign;

        if (Object.keys(attributionParams).length > 0) {
          setAttributionData(attributionParams);
        }
      }
      setTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone);
    }
  }, [location, history, setValue]);

  // Attribution tracking
  useEffect(() => {
    if (Object.keys(attributionData).length > 0) {
      pushAdTrackingEvent({
        event: "signup_page_viewed",
        location,
        ad_platform: advertisePlatform(),
      });
    }
  }, [attributionData, location]);

  // Handle Google OAuth authentication success
  useEffect(() => {
    if (socialAuth?.redirect && socialAuth?.auth) {
      storeSocialAuthTokens(socialAuth.auth);
      const redirectPath = getAuthRedirectPath(socialAuth.auth, query);
      history.push(redirectPath);
      googleAuthReset();
    }
  }, [socialAuth, history, query, googleAuthReset]);

  // Handle Google OAuth errors
  useEffect(() => {
    if (socialAuth?.errorMessage) {
      setServerError(socialAuth.errorMessage);
    }
  }, [socialAuth?.errorMessage]);

  // Smart name splitting function
  const splitFullName = useCallback(fullName => {
    if (!fullName || !fullName.trim()) {
      return { firstName: "", lastName: "" };
    }

    const trimmedName = fullName.trim();
    const nameParts = trimmedName.split(/\s+/);

    if (nameParts.length === 1) {
      // Only one name provided
      return { firstName: nameParts[0], lastName: "" };
    } else if (nameParts.length === 2) {
      // Two names: first and last
      return { firstName: nameParts[0], lastName: nameParts[1] };
    } else {
      // Multiple names: first name is the first part, last name is everything else
      const firstName = nameParts[0];
      const lastName = nameParts.slice(1).join(" ");
      return { firstName, lastName };
    }
  }, []);

  // TagManager initialization and redirect handling
  useEffect(() => {
    TagManager.initialize(tagManagerArgs);
    if (signup?.redirect) {
      if (invitedRequireSignUp && !fromJoinLink) {
        if (signup.token && signup.userData) {
          localStorage.setItem("access_token", signup.token);
          localStorage.setItem("user_id", signup.userData.user_id);
          localStorage.setItem("user_profile", signup.userData.profile_id);
          localStorage.setItem("user_fullName", getFullName(signup.userData));
          localStorage.setItem("email", signup.userData.email);
          if (signup.userData.color) {
            localStorage.setItem("user_profile_color", signup.userData.color);
          }
          setTimeout(() => {
            history.push({
              pathname: "/user/dashboard",
              state: { message: "user-onboard" },
            });
          }, 1000);
        } else {
          history.push(`/auth/login`);
          createNotification("success", "Please login to continue.", 3000);
        }
        resetSignUp();
      } else {
        const ad_platform = advertisePlatform();
        const fullName = watch("fullName");
        const email = watch("email");
        const { firstName, lastName } = splitFullName(fullName);
        history.push(
          `/auth/verify-code?email=${encodeURIComponent(
            email,
          )}&name=${firstName}${
            ad_platform
              ? `&ad_platform=${ad_platform}&last_name=${lastName}`
              : ""
          }`,
        );
        resetSignUp();
      }
    }
  }, [
    invitedRequireSignUp,
    history,
    resetSignUp,
    watch,
    splitFullName,
    signup,
  ]);

  // Password strength calculation
  useEffect(() => {
    let strength = 0;
    if (passwordValue) {
      if (passwordValue.length >= 8) strength += 1;
      if (/[A-Z]/.test(passwordValue)) strength += 1;
      if (/[a-z]/.test(passwordValue)) strength += 1;
      if (/[0-9]/.test(passwordValue)) strength += 1;
      if (/[^A-Za-z0-9]/.test(passwordValue)) strength += 1;
    }
    setPasswordStrength(strength);
  }, [passwordValue]);

  // Moving cursor to the end when password visibility changes
  useEffect(() => {
    if (passwordFieldRef.current) {
      passwordFieldRef.current.selectionStart =
        passwordFieldRef.current.value.length;
      passwordFieldRef.current.selectionEnd =
        passwordFieldRef.current.value.length;
    }
  }, [showPassword]);

  // Auto-focus full name field on component mount
  useEffect(() => {
    if (fullNameRef.current && !isLoaderOpen) {
      fullNameRef.current.focus();
    }
  }, [isLoaderOpen]);

  // Helper function to determine advertising platform
  const advertisePlatform = useCallback(() => {
    if (Object.keys(attributionData).length === 0) return null;
    if (attributionData.gclid) return "google";
    if (attributionData.fbclid) return "facebook";
    if (attributionData.utm_source) {
      const source = attributionData.utm_source.toString().toLowerCase();
      if (source.includes("google")) return "google";
      if (source.includes("facebook") || source.includes("fb"))
        return "facebook";
      return source;
    }
    return null;
  }, [attributionData]);

  const onSubmit = useCallback(
    data => {
      setServerError(null);
      setFieldErrors({});
      setLoading(true);

      if (!turnstileToken) {
        setServerError("Please complete the captcha");
        setLoading(false);
        return;
      }

      // Split the full name into first and last names
      const { firstName, lastName } = splitFullName(data.fullName);

      const payload = {
        first_name: firstName,
        email:
          invitedRequireSignUp && !fromJoinLink ? invitedEmail : data.email,
        password: data.password,
        turnstile_token: turnstileToken,
        ...(lastName && { last_name: lastName }),
        ...(timezone && { timezone }),
        ...(invitedRequireSignUp && { key: invitedKey }),
        ...(Object.keys(attributionData).length > 0 && {
          attribution_data: attributionData,
        }),
      };
      signUpRequest(payload);
    },
    [
      signUpRequest,
      turnstileToken,
      invitedRequireSignUp,
      invitedEmail,
      invitedKey,
      timezone,
      attributionData,
      splitFullName,
      setLoading,
    ],
  );

  const toggleShowPassword = useCallback(() => {
    setShowPassword(prev => !prev);
    if (passwordFieldRef.current) {
      passwordFieldRef.current.focus();
    }
  }, []);

  // Cloudflare Turnstile handlers
  const handleVerify = useCallback(token => {
    setTurnstileToken(token);
    setServerError(null);
  }, []);

  const handleError = useCallback(() => {
    setServerError("Verification error, please try again.");
  }, []);

  const handleExpire = useCallback(() => {
    setTurnstileToken(null);
  }, []);

  const handleKeyDown = useCallback(
    event => {
      if (event.key === "Enter") {
        event.preventDefault();
        handleSubmit(onSubmit)();
      }
    },
    [handleSubmit, onSubmit],
  );

  const handleFieldChange = useCallback(
    (fieldName, onChange) => e => {
      // Clear server errors when user starts typing
      if (fieldErrors[fieldName]) {
        setFieldErrors(prev => ({ ...prev, [fieldName]: null }));
      }
      if (serverError) {
        setServerError(null);
      }
      // Clear signup errors when user starts typing
      if (signup?.errorMessage) {
        resetSignUp();
      }
      onChange(e);
    },
    [fieldErrors, serverError, signup?.errorMessage, resetSignUp],
  );

  // Show loader if checking authentication
  if (isLoaderOpen) {
    return (
      <AuthContainer>
        <SplitAuthCard
          initial={{ opacity: 0, y: 20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
        >
          <div style={{ textAlign: "center", padding: "48px" }}>
            <div style={{ fontSize: "18px", marginBottom: "16px" }}>
              Loading...
            </div>
            <div
              style={{
                width: "40px",
                height: "40px",
                border: "4px solid #f3f3f3",
                borderTop: "4px solid #33ccae",
                borderRadius: "50%",
                animation: "spin 1s linear infinite",
                margin: "0 auto",
              }}
            ></div>
          </div>
        </SplitAuthCard>
      </AuthContainer>
    );
  }

  return (
    <AuthTransition>
      <Helmet>
        <script type="text/javascript">
          {(function () {
            var cdx = document.createElement("script");
            cdx.type = "text/javascript";
            cdx.async = true;
            cdx.src = "https://trk.crozdesk.com/RhfdaLENEesgxi_-YrXo";
            var s = document.getElementsByTagName("script")[0];
            s.parentNode.insertBefore(cdx, s);
          })()}
        </script>
      </Helmet>

      <AuthContainer>
        <AuthCard
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
        >
          <HeaderSection marginBottom="0" alignItems="center">
            <Logo width="243px" height="48px" />
            <Title
              margin="16px 0 0"
              fontSize="20px"
              lineHeight="30px"
              color="#1e2223"
              textAlign="center"
            >
              Create your account
            </Title>
          </HeaderSection>

          <FormSection
            as="form"
            onSubmit={handleSubmit(onSubmit)}
            onKeyDown={handleKeyDown}
            noValidate
            width="400px"
            gap="24px"
          >
            {/* <GoogleAuthButton
              text="Create with Google"
              loginHint={control?._formValues?.email}
            />

            <OrDivider>Or create with email</OrDivider> */}

            <InputContainer>
              <InputGroup>
                <Controller
                  name="fullName"
                  control={control}
                  render={({ field }) => (
                    <StyledInput
                      {...field}
                      ref={fullNameRef}
                      onChange={handleFieldChange("fullName", field.onChange)}
                      type="text"
                      placeholder="Full name"
                      autoComplete="name"
                      hasError={
                        (!!errors.fullName && isSubmitted) ||
                        !!fieldErrors.fullName
                      }
                    />
                  )}
                />
                <AnimatePresence>
                  {fieldErrors.fullName ? (
                    <ErrorMessage
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.2 }}
                    >
                      {fieldErrors.fullName}
                    </ErrorMessage>
                  ) : (
                    errors.fullName &&
                    isSubmitted && (
                      <ErrorMessage
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.2 }}
                      >
                        {errors.fullName.message}
                      </ErrorMessage>
                    )
                  )}
                </AnimatePresence>
              </InputGroup>

              <InputGroup>
                <Controller
                  name="email"
                  control={control}
                  render={({ field }) => (
                    <StyledInput
                      {...field}
                      onChange={handleFieldChange("email", field.onChange)}
                      type="email"
                      placeholder="Email"
                      autoComplete="email"
                      disabled={invitedEmail ? true : false}
                      value={invitedEmail ? invitedEmail : field.value}
                      hasError={
                        (!!errors.email && isSubmitted) || !!fieldErrors.email
                      }
                    />
                  )}
                />
                <AnimatePresence>
                  {fieldErrors.email ? (
                    <ErrorMessage
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.2 }}
                    >
                      {fieldErrors.email}
                    </ErrorMessage>
                  ) : (
                    errors.email &&
                    isSubmitted && (
                      <ErrorMessage
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.2 }}
                      >
                        {errors.email.message}
                      </ErrorMessage>
                    )
                  )}
                </AnimatePresence>
              </InputGroup>

              <InputGroup>
                <PasswordInputContainer>
                  <Controller
                    name="password"
                    control={control}
                    render={({ field }) => (
                      <StyledInput
                        {...field}
                        onChange={handleFieldChange("password", field.onChange)}
                        ref={passwordFieldRef}
                        type={showPassword ? "text" : "password"}
                        placeholder="Password (Min 8 characters)"
                        autoComplete="new-password"
                        hasError={
                          (!!errors.password && isSubmitted) ||
                          !!fieldErrors.password
                        }
                      />
                    )}
                  />
                  <PasswordToggle type="button" onClick={toggleShowPassword}>
                    {showPassword ? (
                      <EyeSlashIcon size="20px" />
                    ) : (
                      <EyeIcon size="20px" />
                    )}
                  </PasswordToggle>
                </PasswordInputContainer>
                <AnimatePresence>
                  {fieldErrors.password ? (
                    <ErrorMessage
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.2 }}
                    >
                      {fieldErrors.password}
                    </ErrorMessage>
                  ) : (
                    errors.password &&
                    isSubmitted && (
                      <ErrorMessage
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.2 }}
                      >
                        {errors.password.message}
                      </ErrorMessage>
                    )
                  )}
                </AnimatePresence>
                {passwordValue && (
                  <PasswordStrengthIndicator password={passwordValue} />
                )}
              </InputGroup>
            </InputContainer>

            {/* Success indicator and Cloudflare branding section */}
            {/* {passwordStrength >= 5 && (
              <SuccessBrandingSection>
                <SuccessIndicator>
                  <TickCircleIcon size="24px" color="#03ce1b" />
                  <SuccessText>Success!</SuccessText>
                </SuccessIndicator>
                <CloudflareBranding>
                  <CloudflareLogo>
                    <img
                      src="https://cf-assets.www.cloudflare.com/cloudflare-brand/assets/logo-standard-wordmark.svg"
                      alt="Cloudflare"
                      style={{
                        height: "26.42px",
                        width: "180.727px",
                        objectFit: "contain",
                      }}
                    />
                  </CloudflareLogo>
                  <PrivacyTermsLinks>
                    <PrivacyTermsLink href="https://www.cloudflare.com/privacy/">
                      Privacy
                    </PrivacyTermsLink>
                    <PrivacyTermsLink href="https://www.cloudflare.com/terms/">
                      Terms
                    </PrivacyTermsLink>
                  </PrivacyTermsLinks>
                </CloudflareBranding>
              </SuccessBrandingSection>
            )} */}

            <AnimatePresence>
              {(serverError || signup?.errorMessage) && (
                <ServerErrorAlert
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                >
                  {serverError || signup?.errorMessage}
                </ServerErrorAlert>
              )}
            </AnimatePresence>

            <Turnstile
              onVerify={handleVerify}
              onError={handleError}
              onExpire={handleExpire}
              className="turnstile-widget"
            />

            <PrimaryButton
              type="submit"
              disabled={isLoading || !turnstileToken}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {isLoading ? "Creating Account..." : "Create Account"}
            </PrimaryButton>

            <AuthSection>
              <AuthText>Already have an account?</AuthText>
              <AuthNavLink to="/auth/login">Sign In</AuthNavLink>
            </AuthSection>
          </FormSection>

          <FooterSection>
            By continuing, you agree to our{" "}
            <TermsLink href="https://fieldservicely.com/terms-of-service">
              Terms
            </TermsLink>{" "}
            &{" "}
            <TermsLink href="https://fieldservicely.com/privacy-policy">
              Privacy Policy
            </TermsLink>
          </FooterSection>
        </AuthCard>
      </AuthContainer>
    </AuthTransition>
  );
};

export default SignUp;
